import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { subscribeToOrders, unsubscribe } from '@/lib/realtime';
import { createOrderOfflineAware, addPaymentOfflineAware, completeOrderOfflineAware } from '@/lib/offline/orderOperations';
import { syncEngine } from '@/lib/offline/sync';
import type { Database } from '@/lib/database.types';

type Order = Database['public']['Tables']['orders']['Row'];
type OrderItem = Database['public']['Tables']['order_items']['Row'];

interface Payment {
  id: string;
  payment_method: string;
  amount: number;
  tip_amount?: number;
  created_at: string;
}

interface OrderWithDetails extends Order {
  items: OrderItem[];
  table?: { name: string } | null;
  customer?: { first_name: string | null; last_name: string | null } | null;
  server?: { first_name: string | null; last_name: string | null; avatar_url?: string | null } | null;
  location?: { id: string; name: string } | null;
  payments?: Payment[];
}

export interface OrderItemInput {
  product_id: string;
  name: string;
  quantity: number;
  unit_price: number;
  notes?: string;
  modifiers?: Array<{ id: string; name: string; price: number }>;
  promotion_id?: string;
  promotion_discount?: number;
}

export interface CreateOrderInput {
  order_type: 'dine_in' | 'takeout' | 'delivery' | 'bar_tab';
  table_number?: string;
  customer_id?: string;
  guest_count?: number;
  notes?: string;
  items: OrderItemInput[];
  discount_amount?: number;
}

interface UseOrdersOptions {
  disableRealtime?: boolean;
  allLocations?: boolean; // When true, fetch orders from all locations
}

export function useOrders(locationId: string | null, options: UseOrdersOptions = {}) {
  const { disableRealtime = false, allLocations = false } = options;
  const queryClient = useQueryClient();

  // Fetch active orders
  const {
    data: orders,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['orders', allLocations ? 'all' : locationId],
    queryFn: async () => {
      // If allLocations is true, fetch all orders; otherwise require a locationId
      if (!allLocations && !locationId) return [];

      let query = supabase
        .from('orders')
        .select(`
          *,
          items:order_items(*),
          table:tables(name),
          customer:customers(first_name, last_name),
          server:profiles!orders_user_id_fkey(first_name, last_name, avatar_url),
          location:locations(id, name),
          payments(id, payment_method, amount, tip_amount, created_at)
        `)
        .order('created_at', { ascending: false })
        .limit(500);

      // Only filter by location if not fetching all
      if (!allLocations && locationId) {
        query = query.eq('location_id', locationId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as OrderWithDetails[];
    },
    enabled: allLocations || !!locationId,
  });

  // Subscribe to realtime updates (skip for kiosk mode)
  useEffect(() => {
    if (!locationId || disableRealtime) return;

    const channel = subscribeToOrders(locationId, () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
    });

    return () => {
      unsubscribe(channel);
    };
  }, [locationId, queryClient, disableRealtime]);

  // Create order mutation
  const createOrder = useMutation({
    mutationFn: async (orderData: {
      order_type: Order['order_type'];
      table_id?: string;
      customer_id?: string;
      guest_count?: number;
    }) => {
      if (!locationId) throw new Error('No location selected');

      // Generate order number
      const { data: orderNumber, error: rpcError } = await supabase.rpc(
        'generate_order_number',
        { p_location_id: locationId } as any
      );

      if (rpcError) throw rpcError;

      const { data, error } = await supabase
        .from('orders')
        .insert({
          location_id: locationId,
          order_number: orderNumber,
          ...orderData,
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
    },
  });

  // Add item to order
  const addItem = useMutation({
    mutationFn: async ({
      orderId,
      productId,
      quantity,
      modifiers,
      notes,
    }: {
      orderId: string;
      productId: string;
      quantity: number;
      modifiers?: Array<{ id: string; name: string; price: number }>;
      notes?: string;
    }) => {
      // Get product details
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('name, price, tax_rate')
        .eq('id', productId)
        .single();

      if (productError) throw productError;

      const modifierTotal = modifiers?.reduce((sum, m) => sum + m.price, 0) ?? 0;
      const unitPrice = (product as any).price + modifierTotal;
      const taxRate = (product as any).tax_rate ?? 0;
      const taxAmount = unitPrice * quantity * taxRate;
      const total = unitPrice * quantity + taxAmount;

      const { data, error } = await supabase
        .from('order_items')
        .insert({
          order_id: orderId,
          product_id: productId,
          name: (product as any).name,
          quantity,
          unit_price: unitPrice,
          modifiers: modifiers ?? [],
          tax_amount: taxAmount,
          total,
          notes,
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
    },
  });

  // Update item
  const updateItem = useMutation({
    mutationFn: async ({
      itemId,
      updates,
    }: {
      itemId: string;
      updates: Partial<OrderItem>;
    }) => {
      const { data, error } = await (supabase
        .from('order_items') as any)
        .update(updates)
        .eq('id', itemId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
    },
  });

  // Void item
  const voidItem = useMutation({
    mutationFn: async ({ itemId, reason }: { itemId: string; reason?: string }) => {
      const { data, error } = await (supabase
        .from('order_items') as any)
        .update({
          status: 'voided',
          notes: reason ? `[VOIDED: ${reason}]` : undefined,
        })
        .eq('id', itemId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
    },
  });

  // Send to kitchen (existing order)
  const sendToKitchen = useMutation({
    mutationFn: async ({ orderId }: { orderId: string }) => {
      // Get the order items to send
      const { data: items, error: fetchError } = await supabase
        .from('order_items')
        .select(`
          id,
          name,
          quantity,
          modifiers,
          notes,
          product:products(id, category_id, send_to_kitchen)
        `)
        .eq('order_id', orderId)
        .eq('status', 'pending');

      if (fetchError) throw fetchError;
      if (!items || items.length === 0) {
        throw new Error('No pending items to send');
      }

      // Get kitchen stations for routing
      const { data: order } = await supabase
        .from('orders')
        .select('location_id')
        .eq('id', orderId)
        .single();

      if (!order) throw new Error('Order not found');

      const { data: stations } = await supabase
        .from('kitchen_stations')
        .select('id, name, display_categories')
        .eq('location_id', order.location_id)
        .eq('is_active', true);

      // Route items to stations using priority:
      // 1. Product's direct station_id assignment (highest priority)
      // 2. Station's display_categories matching product's category
      // 3. Default "All" station or first active station (fallback)
      const stationOrders = new Map<string, typeof items>();

      for (const item of items) {
        // Product might be returned as object or array depending on join
        const rawProduct = item.product as any;
        const product = Array.isArray(rawProduct) ? rawProduct[0] : rawProduct;

        // Skip products that have send_to_kitchen disabled
        if (product?.send_to_kitchen === false) continue;

        const productStationId = product?.station_id;
        const categoryId = product?.category_id;

        let targetStation = null;

        // Priority 1: Direct product station assignment
        if (productStationId) {
          targetStation = stations?.find((s: any) => s.id === productStationId);
        }

        // Priority 2: Category-based routing
        if (!targetStation && categoryId) {
          targetStation = stations?.find((s: any) => {
            if (!s.display_categories || s.display_categories.length === 0) return false;
            return s.display_categories.includes(categoryId);
          });
        }

        // Priority 3: Fallback to "All" station or first active station
        if (!targetStation) {
          targetStation = stations?.find((s: any) => s.name.toLowerCase() === 'all') || stations?.[0];
        }

        if (targetStation) {
          if (!stationOrders.has(targetStation.id)) {
            stationOrders.set(targetStation.id, []);
          }
          stationOrders.get(targetStation.id)!.push(item);
        }
      }

      // Create kitchen orders
      for (const [stationId, stationItems] of stationOrders) {
        await supabase
          .from('kitchen_orders')
          .insert({
            order_id: orderId,
            station_id: stationId,
            items: stationItems.map((item: any) => ({
              id: item.id,
              name: item.name,
              quantity: item.quantity,
              modifiers: item.modifiers,
              notes: item.notes,
            })),
            status: 'pending',
            sent_at: new Date().toISOString(),
          });
      }

      // Update pending items to sent
      const { error: itemsError } = await supabase
        .from('order_items')
        .update({
          status: 'sent',
          sent_to_kitchen_at: new Date().toISOString(),
        })
        .eq('order_id', orderId)
        .eq('status', 'pending');

      if (itemsError) throw itemsError;

      // Update order status
      const { data, error } = await supabase
        .from('orders')
        .update({ status: 'sent' })
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders'] });
    },
  });

  // Create order and send to kitchen in one go (for POS)
  const createOrderAndSendToKitchen = useMutation({
    mutationFn: async (input: CreateOrderInput) => {
      if (!locationId) throw new Error('No location selected');

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate order number
      const { data: orderNumber, error: rpcError } = await supabase.rpc(
        'generate_order_number',
        { p_location_id: locationId }
      );

      if (rpcError) throw rpcError;

      // Calculate totals
      const subtotal = input.items.reduce((sum, item) => {
        const modifierTotal = item.modifiers?.reduce((m, mod) => m + mod.price, 0) ?? 0;
        return sum + (item.unit_price + modifierTotal) * item.quantity;
      }, 0);
      const taxRate = 0.15; // 15% GCT
      const taxAmount = subtotal * taxRate;
      const total = subtotal + taxAmount;

      // Convert order type from POS format to DB format
      const orderTypeMap: Record<string, string> = {
        'dine-in': 'dine_in',
        'takeout': 'takeout',
        'delivery': 'delivery',
        'dine_in': 'dine_in',
        'bar_tab': 'bar_tab',
      };
      const dbOrderType = orderTypeMap[input.order_type] || 'dine_in';

      // Create the order
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert({
          location_id: locationId,
          user_id: user.id,
          order_number: orderNumber,
          order_type: dbOrderType,
          customer_id: input.customer_id,
          guest_count: input.guest_count || 1,
          notes: input.notes,
          subtotal,
          tax_amount: taxAmount,
          total,
          status: 'open',
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Create order items
      const orderItems = input.items.map(item => {
        const modifierTotal = item.modifiers?.reduce((m, mod) => m + mod.price, 0) ?? 0;
        const unitPrice = item.unit_price + modifierTotal;
        const itemTax = unitPrice * item.quantity * taxRate;
        const itemTotal = unitPrice * item.quantity + itemTax;

        return {
          order_id: order.id,
          product_id: item.product_id,
          name: item.name,
          quantity: item.quantity,
          unit_price: unitPrice,
          modifiers: item.modifiers || [],
          tax_amount: itemTax,
          total: itemTotal,
          notes: item.notes,
          status: 'pending',
        };
      });

      const { data: insertedItems, error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems)
        .select();

      if (itemsError) throw itemsError;

      // Get kitchen stations
      const { data: stations } = await supabase
        .from('kitchen_stations')
        .select('id, name, display_categories')
        .eq('location_id', locationId)
        .eq('is_active', true);

      // Get product info for routing (including station_id for direct assignment)
      const productIds = input.items.map(i => i.product_id);
      const { data: products } = await supabase
        .from('products')
        .select('id, category_id, station_id, send_to_kitchen')
        .in('id', productIds);

      const productInfo = new Map(products?.map((p: any) => [p.id, { category_id: p.category_id, station_id: p.station_id, send_to_kitchen: p.send_to_kitchen }]) || []);

      // Route items to stations using priority:
      // 1. Product's direct station_id assignment (highest priority)
      // 2. Station's display_categories matching product's category
      // 3. Default "All" station or first active station (fallback)
      const stationOrders = new Map<string, any[]>();

      for (let i = 0; i < insertedItems.length; i++) {
        const item = insertedItems[i];
        const info = productInfo.get(input.items[i].product_id);

        // Skip products that have send_to_kitchen disabled
        if (info?.send_to_kitchen === false) continue;

        const productStationId = info?.station_id;
        const categoryId = info?.category_id;

        let targetStation = null;

        // Priority 1: Direct product station assignment
        if (productStationId) {
          targetStation = stations?.find((s: any) => s.id === productStationId);
        }

        // Priority 2: Category-based routing
        if (!targetStation && categoryId) {
          targetStation = stations?.find((s: any) => {
            if (!s.display_categories || s.display_categories.length === 0) return false;
            return s.display_categories.includes(categoryId);
          });
        }

        // Priority 3: Fallback to "All" station or first active station
        if (!targetStation) {
          targetStation = stations?.find((s: any) => s.name.toLowerCase() === 'all') || stations?.[0];
        }

        if (targetStation) {
          if (!stationOrders.has(targetStation.id)) {
            stationOrders.set(targetStation.id, []);
          }
          stationOrders.get(targetStation.id)!.push({
            ...item,
            originalInput: input.items[i],
          });
        }
      }

      // Create kitchen orders for each station
      for (const [stationId, stationItems] of stationOrders) {
        await supabase
          .from('kitchen_orders')
          .insert({
            order_id: order.id,
            station_id: stationId,
            items: stationItems.map((item: any) => ({
              id: item.id,
              name: item.name,
              quantity: item.quantity,
              modifiers: item.modifiers,
              notes: item.notes,
            })),
            status: 'pending',
            priority: 0,
            sent_at: new Date().toISOString(),
          });
      }

      // Update items status to sent
      await supabase
        .from('order_items')
        .update({
          status: 'sent',
          sent_to_kitchen_at: new Date().toISOString(),
        })
        .eq('order_id', order.id);

      // Update order status to sent
      const { data: updatedOrder, error: updateError } = await supabase
        .from('orders')
        .update({ status: 'sent' })
        .eq('id', order.id)
        .select()
        .single();

      if (updateError) throw updateError;

      return { order: updatedOrder, itemsSent: insertedItems.length };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders'] });
    },
  });

  // Create order with offline support (for POS when network may be unreliable)
  const createOrderOffline = useMutation({
    mutationFn: async (input: CreateOrderInput) => {
      if (!locationId) throw new Error('No location selected');

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Use offline-aware order creation
      const result = await createOrderOfflineAware(input, locationId, user.id);

      return {
        order: result.order,
        itemsSent: result.itemsSent,
        isOffline: result.isOffline,
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
      if (!data.isOffline) {
        queryClient.invalidateQueries({ queryKey: ['kitchen-orders'] });
      }
    },
  });

  // Check if currently online
  const isOnline = syncEngine.state.isOnline;

  // Add payment (with offline support)
  const addPayment = useMutation({
    mutationFn: async ({
      orderId,
      paymentMethod,
      amount,
      tipAmount,
      paymentTypeId,
    }: {
      orderId: string;
      paymentMethod: 'cash' | 'card' | 'gift_card' | 'loyalty' | 'store_credit' | 'other';
      amount: number;
      tipAmount?: number;
      paymentTypeId?: string;
    }) => {
      // Use offline-aware payment creation
      const result = await addPaymentOfflineAware(orderId, paymentMethod, amount, tipAmount, paymentTypeId);

      if (!result.success) {
        throw new Error('Failed to add payment');
      }

      // Update order tip if provided and online
      if (tipAmount && tipAmount > 0 && !result.isOffline) {
        await (supabase
          .from('orders') as any)
          .update({
            tip_amount: supabase.rpc('increment_tip', { order_id: orderId, amount: tipAmount } as any),
          })
          .eq('id', orderId);
      }

      return { isOffline: result.isOffline };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
    },
  });

  // Complete order (with offline support)
  const completeOrder = useMutation({
    mutationFn: async ({ orderId }: { orderId: string }) => {
      // Use offline-aware order completion
      const result = await completeOrderOfflineAware(orderId);

      if (!result.success) {
        throw new Error('Failed to complete order');
      }

      // Update customer stats only when online (will sync later if offline)
      if (!result.isOffline) {
        try {
          // Get the order to access customer_id and total
          const { data: order } = await supabase
            .from('orders')
            .select('id, customer_id, total')
            .eq('id', orderId)
            .single();

          // Update customer stats if customer is linked
          if (order?.customer_id) {
            const { data: customer } = await supabase
              .from('customers')
              .select('total_visits, total_spent')
              .eq('id', order.customer_id)
              .single();

            if (customer) {
              const newTotalVisits = (customer.total_visits || 0) + 1;
              const newTotalSpent = (customer.total_spent || 0) + (order.total || 0);
              const newAverageTicket = newTotalSpent / newTotalVisits;

              await supabase
                .from('customers')
                .update({
                  total_visits: newTotalVisits,
                  total_spent: newTotalSpent,
                  average_ticket: newAverageTicket,
                  last_visit_at: new Date().toISOString(),
                })
                .eq('id', order.customer_id);
            }
          }
        } catch (e) {
          // Customer stats update is non-critical, don't fail the completion
          console.warn('[Order] Failed to update customer stats:', e);
        }
      }

      return { isOffline: result.isOffline };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  // Cancel order
  const cancelOrder = useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason?: string }) => {
      const { data, error } = await (supabase
        .from('orders') as any)
        .update({
          status: 'cancelled',
          notes: reason ? `[CANCELLED: ${reason}]` : undefined,
        })
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
    },
  });

  // Duplicate order - creates a new order with the same items (status: open)
  const duplicateOrder = useMutation({
    mutationFn: async ({ orderId }: { orderId: string }) => {
      if (!locationId) throw new Error('No location selected');

      // Get the original order with items
      const { data: originalOrder, error: fetchError } = await supabase
        .from('orders')
        .select(`
          *,
          items:order_items(*)
        `)
        .eq('id', orderId)
        .single();

      if (fetchError) throw fetchError;
      if (!originalOrder) throw new Error('Original order not found');

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate new order number
      const { data: orderNumber, error: rpcError } = await supabase.rpc(
        'generate_order_number',
        { p_location_id: locationId }
      );

      if (rpcError) throw rpcError;

      // Create new order (status: open, not sent to kitchen)
      const { data: newOrder, error: orderError } = await supabase
        .from('orders')
        .insert({
          location_id: locationId,
          user_id: user.id,
          order_number: orderNumber,
          order_type: originalOrder.order_type,
          customer_id: originalOrder.customer_id,
          table_id: originalOrder.table_id,
          guest_count: originalOrder.guest_count,
          notes: `Duplicated from Order #${originalOrder.order_number}`,
          subtotal: originalOrder.subtotal,
          tax_amount: originalOrder.tax_amount,
          discount_amount: 0, // Reset discounts for new order
          tip_amount: 0,
          total: originalOrder.subtotal + originalOrder.tax_amount,
          status: 'open',
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Duplicate order items
      const originalItems = originalOrder.items || [];
      if (originalItems.length > 0) {
        const newItems = originalItems
          .filter((item: any) => item.status !== 'voided') // Don't duplicate voided items
          .map((item: any) => ({
            order_id: newOrder.id,
            product_id: item.product_id,
            variant_id: item.variant_id,
            name: item.name,
            quantity: item.quantity,
            unit_price: item.unit_price,
            modifiers: item.modifiers,
            discount_amount: 0,
            tax_amount: item.tax_amount,
            total: item.total,
            notes: item.notes,
            seat_number: item.seat_number,
            course_number: item.course_number,
            status: 'pending', // Reset to pending
          }));

        if (newItems.length > 0) {
          const { error: itemsError } = await supabase
            .from('order_items')
            .insert(newItems);

          if (itemsError) throw itemsError;
        }
      }

      return { order: newOrder, originalOrderNumber: originalOrder.order_number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
    },
  });

  // Void order - cancels order with manager approval and audit trail
  const voidOrder = useMutation({
    mutationFn: async ({
      orderId,
      reason,
      approvedBy,
      restoreInventory = false,
    }: {
      orderId: string;
      reason: string;
      approvedBy: string;
      restoreInventory?: boolean;
    }) => {
      // Get the order to check current state
      const { data: order, error: fetchError } = await supabase
        .from('orders')
        .select('*, items:order_items(*, product:products(id, name))')
        .eq('id', orderId)
        .single();

      if (fetchError) throw fetchError;
      if (!order) throw new Error('Order not found');

      // Can't void already completed/cancelled orders
      if (['cancelled', 'refunded'].includes(order.status)) {
        throw new Error('Order is already cancelled or refunded');
      }

      // Void all associated kitchen orders
      await supabase
        .from('kitchen_orders')
        .update({ status: 'completed' }) // Mark as completed to remove from KDS
        .eq('order_id', orderId)
        .in('status', ['pending', 'in_progress', 'ready']);

      // Void all order items
      await supabase
        .from('order_items')
        .update({ status: 'voided' })
        .eq('order_id', orderId)
        .neq('status', 'voided');

      // Restore inventory if requested
      if (restoreInventory && order.items && order.items.length > 0) {
        const { data: { user: actingUser } } = await supabase.auth.getUser();

        for (const item of order.items) {
          if (!item.product_id) continue;

          // Get the inventory item for this product at this location
          const { data: inventoryItem } = await supabase
            .from('inventory_items')
            .select('id, quantity_on_hand, quantity_boh, quantity_foh')
            .eq('product_id', item.product_id)
            .eq('location_id', locationId)
            .single();

          if (inventoryItem) {
            const currentBoh = inventoryItem.quantity_boh ?? 0;
            const currentFoh = inventoryItem.quantity_foh ?? inventoryItem.quantity_on_hand ?? 0;
            const quantityBefore = currentBoh + currentFoh;
            const newFoh = currentFoh + item.quantity;
            const newQuantity = currentBoh + newFoh;

            // Restore stock to FOH and keep total in sync with BOH + FOH
            await supabase
              .from('inventory_items')
              .update({
                quantity_foh: newFoh,
                quantity_on_hand: newQuantity,
                updated_at: new Date().toISOString(),
              })
              .eq('id', inventoryItem.id);

            // Log inventory transaction
            await supabase.from('inventory_transactions').insert({
              inventory_item_id: inventoryItem.id,
              user_id: actingUser?.id,
              transaction_type: 'returned',
              quantity_change: item.quantity,
              quantity_before: quantityBefore,
              quantity_after: newQuantity,
              reference_type: 'void',
              reference_id: orderId,
              notes: `Void order #${order.order_number}: ${reason}`,
            });
          }
        }
      }

      // Update order status to cancelled with void metadata
      const voidNote = `[VOIDED: ${reason}] Approved by: ${approvedBy} at ${new Date().toISOString()}${restoreInventory ? ' (Inventory restored)' : ''}`;
      const existingNotes = order.notes ? `${order.notes}\n` : '';

      const { data, error } = await supabase
        .from('orders')
        .update({
          status: 'cancelled',
          voided_at: new Date().toISOString(),
          void_reason: reason,
          void_approved_by: approvedBy,
          notes: existingNotes + voidNote,
        })
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;

      // Log audit event
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from('audit_logs').insert({
        user_id: user?.id,
        action: 'void_order',
        entity_type: 'order',
        entity_id: orderId,
        changes: {
          reason,
          approved_by: approvedBy,
          previous_status: order.status,
          order_total: order.total,
          inventory_restored: restoreInventory,
        },
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  // Refund order mutation
  const refundOrder = useMutation({
    mutationFn: async ({
      orderId,
      amount,
      reason,
      refundMethod,
      approvedBy,
    }: {
      orderId: string;
      amount: number;
      reason: string;
      refundMethod: string;
      approvedBy: string;
    }) => {
      // Get the order to check current state
      const { data: order, error: fetchError } = await supabase
        .from('orders')
        .select(`
          *,
          customer:customers(id, total_spent, total_visits),
          payments(id, payment_method, amount)
        `)
        .eq('id', orderId)
        .single();

      if (fetchError) throw fetchError;
      if (!order) throw new Error('Order not found');

      // Can only refund completed orders
      if (order.status !== 'completed') {
        throw new Error('Only completed orders can be refunded');
      }

      // Can't refund more than the order total
      if (amount > order.total) {
        throw new Error('Refund amount cannot exceed order total');
      }

      // Determine actual refund method
      const actualRefundMethod = refundMethod === 'original'
        ? (order.payments?.[0]?.payment_method || 'cash')
        : refundMethod;

      // Create refund record
      const { data: refund, error: refundError } = await supabase
        .from('refunds')
        .insert({
          order_id: orderId,
          payment_id: order.payments?.[0]?.id || null,
          user_id: (await supabase.auth.getUser()).data.user?.id || null,
          amount,
          reason,
          refund_method: actualRefundMethod,
          status: 'completed',
        })
        .select()
        .single();

      if (refundError) throw refundError;

      // Update order status to refunded (or keep completed if partial)
      const isFullRefund = amount >= order.total;
      const refundNote = `[REFUNDED: ${reason}] Amount: ${amount}, Method: ${actualRefundMethod}, Approved by: ${approvedBy} at ${new Date().toISOString()}`;
      const existingNotes = order.notes ? `${order.notes}\n` : '';

      const { data: updatedOrder, error: updateError } = await supabase
        .from('orders')
        .update({
          status: isFullRefund ? 'refunded' : order.status,
          notes: existingNotes + refundNote,
        })
        .eq('id', orderId)
        .select()
        .single();

      if (updateError) throw updateError;

      // Restore inventory for refunded items
      const { error: inventoryError } = await supabase.rpc('restore_inventory_on_refund', {
        p_order_id: orderId,
        p_refund_type: isFullRefund ? 'full' : 'partial',
        p_refund_amount: isFullRefund ? null : amount,
      });
      
      if (inventoryError) {
        console.error('Failed to restore inventory on refund:', inventoryError);
        // Don't throw - refund is still valid even if inventory restoration fails
      }

      // Update customer stats if customer exists
      if (order.customer?.id) {
        const newTotalSpent = Math.max(0, (order.customer.total_spent || 0) - amount);
        await supabase
          .from('customers')
          .update({
            total_spent: newTotalSpent,
            updated_at: new Date().toISOString(),
          })
          .eq('id', order.customer.id);
      }

      // Log audit event
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from('audit_logs').insert({
        user_id: user?.id,
        location_id: locationId,
        action: 'refund_order',
        entity_type: 'order',
        entity_id: orderId,
        changes: {
          refund_id: refund.id,
          amount,
          reason,
          refund_method: actualRefundMethod,
          approved_by: approvedBy,
          is_full_refund: isFullRefund,
          order_total: order.total,
        },
      });

      return { order: updatedOrder, refund };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['register-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory'] });
    },
  });

  return {
    orders: orders ?? [],
    isLoading,
    error,
    refetch,
    isOnline,
    createOrder,
    addItem,
    updateItem,
    voidItem,
    sendToKitchen,
    createOrderAndSendToKitchen,
    createOrderOffline,
    addPayment,
    completeOrder,
    cancelOrder,
    duplicateOrder,
    voidOrder,
    refundOrder,
  };
}
